import React from 'react';

/*
 * Original, hand-coded inline SVG illustrations for the Family Command Center
 * and Open Student Assistant experiences.
 *
 * Style guide: flat editorial linework, 2px consistent stroke, 2-3 color fills
 * per piece, no gradients/glow/photoreal faces. Colors are read from CSS custom
 * properties (--illus-1/2/3/ink/paper) so illustrations automatically adapt to
 * the parent ("calm") and student ("warm") experience palettes defined in
 * styles.css, and to dark mode.
 *
 * Accessibility: every illustration accepts a `title`. When `decorative` is
 * true (the default for small inline icons that sit next to a text heading
 * that already says the same thing), the SVG is hidden from the accessibility
 * tree via aria-hidden. When `decorative` is false, the SVG is exposed as an
 * image with a real accessible name via role="img" + aria-label — use this
 * for larger illustrations that carry meaning on their own (e.g. the hero
 * artwork on an empty state or greeting banner).
 */

type IllustrationProps = {
  title: string;
  className?: string;
  decorative?: boolean;
};

function Svg({
  title,
  className,
  decorative = true,
  viewBox = '0 0 96 96',
  children,
}: IllustrationProps & { viewBox?: string; children: React.ReactNode }) {
  return (
    <svg
      className={`illustration${className ? ` ${className}` : ''}`}
      viewBox={viewBox}
      width="100%"
      height="100%"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : title}
      aria-hidden={decorative ? 'true' : undefined}
    >
      <title>{title}</title>
      {children}
    </svg>
  );
}

/** A backpack with a notebook peeking out — "ready for today". */
export function BackpackIllustration(props: IllustrationProps) {
  return (
    <Svg {...props} title={props.title ?? 'A packed backpack, ready for the day'}>
      <rect x="24" y="30" width="48" height="52" rx="14" fill="var(--illus-2)" stroke="var(--illus-ink)" strokeWidth="2.5" />
      <path d="M34 30v-6a14 14 0 0 1 28 0v6" stroke="var(--illus-ink)" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="40" y="20" width="16" height="10" rx="4" fill="var(--illus-1)" stroke="var(--illus-ink)" strokeWidth="2" />
      <rect x="32" y="46" width="32" height="22" rx="4" fill="var(--illus-paper)" stroke="var(--illus-ink)" strokeWidth="2" />
      <path d="M38 52h20M38 58h20M38 64h12" stroke="var(--illus-ink)" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 44h48" stroke="var(--illus-ink)" strokeWidth="2" />
      <circle cx="48" cy="40" r="3" fill="var(--illus-3)" stroke="var(--illus-ink)" strokeWidth="1.5" />
    </Svg>
  );
}

/** A stack of notebooks and a pencil — "your work". */
export function NotebookIllustration(props: IllustrationProps) {
  return (
    <Svg {...props} title={props.title ?? 'A stack of notebooks and a pencil'}>
      <rect x="18" y="52" width="52" height="14" rx="3" fill="var(--illus-1)" stroke="var(--illus-ink)" strokeWidth="2" />
      <rect x="22" y="38" width="48" height="14" rx="3" fill="var(--illus-2)" stroke="var(--illus-ink)" strokeWidth="2" />
      <rect x="26" y="24" width="44" height="14" rx="3" fill="var(--illus-paper)" stroke="var(--illus-ink)" strokeWidth="2" />
      <path d="M34 31h20M34 45h18M30 59h20" stroke="var(--illus-ink)" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
      <path d="M66 20l10 10-30 30-12 2 2-12 30-30z" fill="var(--illus-3)" stroke="var(--illus-ink)" strokeWidth="2" strokeLinejoin="round" />
    </Svg>
  );
}

/** A calendar with a star on today. */
export function CalendarStarIllustration(props: IllustrationProps) {
  return (
    <Svg {...props} title={props.title ?? "A calendar with today's date starred"}>
      <rect x="18" y="22" width="60" height="54" rx="8" fill="var(--illus-paper)" stroke="var(--illus-ink)" strokeWidth="2.5" />
      <path d="M18 36h60" stroke="var(--illus-ink)" strokeWidth="2.5" />
      <path d="M30 16v12M66 16v12" stroke="var(--illus-ink)" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="28" y="44" width="10" height="10" rx="2" fill="var(--illus-1)" opacity="0.8" />
      <rect x="43" y="44" width="10" height="10" rx="2" fill="var(--illus-1)" opacity="0.5" />
      <rect x="58" y="44" width="10" height="10" rx="2" fill="var(--illus-1)" opacity="0.8" />
      <rect x="28" y="59" width="10" height="10" rx="2" fill="var(--illus-1)" opacity="0.5" />
      <path
        d="M53 55l3.2 6.5 7.2 1-5.2 5 1.2 7.1L53 71l-6.4 3.6 1.2-7.1-5.2-5 7.2-1L53 55z"
        fill="var(--illus-3)"
        stroke="var(--illus-ink)"
        strokeWidth="2"
        strokeLinejoin="round"
        transform="translate(0 -6)"
      />
    </Svg>
  );
}

/** A seedling in a pot — growth and progress, used for empty/first-run states. */
export function SeedlingIllustration(props: IllustrationProps) {
  return (
    <Svg {...props} title={props.title ?? 'A small seedling growing in a pot'}>
      <path d="M48 60V38" stroke="var(--illus-ink)" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M48 44c-8-2-14-10-14-18 10 0 16 8 14 18z" fill="var(--illus-2)" stroke="var(--illus-ink)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M48 40c8-2 13-9 13-16-9 0-15 7-13 16z" fill="var(--illus-3)" stroke="var(--illus-ink)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M34 60h28l-4 20a4 4 0 0 1-4 3.4H42a4 4 0 0 1-4-3.4L34 60z" fill="var(--illus-1)" stroke="var(--illus-ink)" strokeWidth="2.5" strokeLinejoin="round" />
    </Svg>
  );
}

/** A paper airplane leaving a trail — encouragement / "sent" / progress. */
export function PaperAirplaneIllustration(props: IllustrationProps) {
  return (
    <Svg {...props} title={props.title ?? 'A paper airplane flying with a gentle trail'}>
      <path d="M14 62c14-4 26-10 34-18" stroke="var(--illus-ink)" strokeWidth="2" strokeDasharray="1 7" strokeLinecap="round" opacity="0.6" />
      <path d="M46 22 84 40 62 48l-6 22-7-14-14-4 21-30z" fill="var(--illus-1)" stroke="var(--illus-ink)" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M56 48 46 56" stroke="var(--illus-ink)" strokeWidth="2" strokeLinecap="round" />
    </Svg>
  );
}

/** A small home with a family of simple, diverse figures beneath it — trust and belonging. */
export function HomeFamilyIllustration(props: IllustrationProps) {
  return (
    <Svg {...props} title={props.title ?? 'A cozy home watching over a family of three'} viewBox="0 0 120 96">
      <path d="M60 10 20 40h8v38a4 4 0 0 0 4 4h56a4 4 0 0 0 4-4V40h8L60 10z" fill="var(--illus-2)" stroke="var(--illus-ink)" strokeWidth="2.5" strokeLinejoin="round" />
      <rect x="52" y="58" width="16" height="24" rx="2" fill="var(--illus-paper)" stroke="var(--illus-ink)" strokeWidth="2" />
      <rect x="34" y="48" width="12" height="12" rx="2" fill="var(--illus-1)" stroke="var(--illus-ink)" strokeWidth="2" />
      <rect x="74" y="48" width="12" height="12" rx="2" fill="var(--illus-1)" stroke="var(--illus-ink)" strokeWidth="2" />
      {/* simple, abstracted family silhouettes — varied heights and one wheelchair user */}
      <g stroke="var(--illus-ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="30" cy="70" r="5" fill="var(--illus-3)" />
        <path d="M30 75v10M24 92l6-7 6 7M24 82h12" fill="none" />
        <circle cx="46" cy="66" r="6" fill="var(--illus-1)" />
        <path d="M46 72v12M39 92l7-8 7 8M39 80h14" fill="none" />
        <circle cx="90" cy="70" r="5" fill="var(--illus-2)" />
        <path d="M90 75v6" fill="none" />
        <circle cx="90" cy="88" r="8" fill="none" />
        <path d="M90 81v3M84 92h-2M96 92h2" fill="none" />
      </g>
    </Svg>
  );
}

/** A shield with a heart — privacy, safety, and trust. */
export function ShieldHeartIllustration(props: IllustrationProps) {
  return (
    <Svg {...props} title={props.title ?? 'A shield with a heart, representing privacy and trust'}>
      <path
        d="M48 12 76 22v22c0 20-12 32-28 34-16-2-28-14-28-34V22z"
        fill="var(--illus-2)"
        stroke="var(--illus-ink)"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path
        d="M48 52c-9-6-15-11-15-18a8 8 0 0 1 15-4 8 8 0 0 1 15 4c0 7-6 12-15 18z"
        fill="var(--illus-3)"
        stroke="var(--illus-ink)"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** A speech bubble with a small heart dot — friendly text conversation. */
export function ChatBubbleIllustration(props: IllustrationProps) {
  return (
    <Svg {...props} title={props.title ?? 'A friendly speech bubble'}>
      <path
        d="M20 26h56a6 6 0 0 1 6 6v28a6 6 0 0 1-6 6H46l-14 12V66H20a6 6 0 0 1-6-6V32a6 6 0 0 1 6-6z"
        fill="var(--illus-1)"
        stroke="var(--illus-ink)"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <circle cx="34" cy="46" r="3.4" fill="var(--illus-ink)" />
      <circle cx="48" cy="46" r="3.4" fill="var(--illus-ink)" />
      <circle cx="62" cy="46" r="3.4" fill="var(--illus-ink)" />
    </Svg>
  );
}

export function AllDoneIllustration(props: IllustrationProps) {
  return (
    <Svg {...props} title={props.title ?? 'A checkmark, showing everything is done'}>
      <circle cx="48" cy="48" r="30" fill="var(--illus-1)" stroke="var(--illus-ink)" strokeWidth="2.5" />
      <path d="M34 49l10 10 20-22" stroke="var(--illus-ink)" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
      <g stroke="var(--illus-3)" strokeWidth="2.4" strokeLinecap="round">
        <path d="M48 8v6M48 82v6M8 48h6M82 48h6" />
        <path d="M18 18l4 4M74 74l4 4M74 18l-4 4M18 74l4 4" />
      </g>
    </Svg>
  );
}
